import { useState, FormEvent, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";

import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import {
  Container,
  Dialog,
  DialogContentText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import conf from "../../conf";
import Spinner from "../utils/Spinner";
import InfoChip from "../utils/InfoChip";
import ProviderSelect from "./ProviderSelect";
import SectionHeader from "../utils/SectionHeader";
import {
  IdpConfig,
  useConfgSysOrganizationCreate,
} from "../../hooks/confg/base";

const CreateView = (props: any) => {
  const { open, onClose } = props;

  const [locked, setLocked] = useState(false);

  const [idp, setIdp] = useState<IdpConfig[]>([]);
  const [name, setName] = useState<string>("");
  const [domain, setDomain] = useState<string>("");
  const [acronym, setAcronym] = useState<string>("");
  const [urlPublic, setUrlPublic] = useState<string>("");
  const [urlPrivate, setUrlPrivate] = useState<string>("");
  const [dlmToken, setDlmToken] = useState<string>("");
  const [dlmPushUrl, setDlmPushUrl] = useState<string>("");
  const [dlmVerifyUrl, setDlmVerifyUrl] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const create = useConfgSysOrganizationCreate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    create.mutate({
      name,
      domain,
      acronym,
      urlPublic,
      urlPrivate,
      description,
      idp,
      dlm: {
        token: dlmToken,
        pushUrl: dlmPushUrl,
        verifyUrl: dlmVerifyUrl,
      },
    });
  };

  const resetForm = () => {
    setLocked(false);
    setDescription("");
    setName("");
    setDomain("");
    setAcronym("");
    setUrlPublic("");
    setUrlPrivate("");
    setDlmToken("");
    setDlmPushUrl("");
    setDlmVerifyUrl("");
    setIdp([]);
  };

  const domainIcon = (
    <InputAdornment position="end">
      <InfoChip
        size="medium"
        info="This attribute is used to specify identity provider validity."
      />
    </InputAdornment>
  );

  useEffect(() => {
    if (create.isSuccess) {
      resetForm();
      onClose();
    }
  }, [create.isSuccess]);

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="organization-create-header"
          title="Create: Organization"
          icon={<CreateNewFolderIcon />}
          buttons={[
            <Button
              key={"organization-create-reset"}
              color="inherit"
              size="small"
              disabled={locked}
              onClick={resetForm}
            >
              Reset
            </Button>,

            <Button
              key={"organization-create-close"}
              size="small"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon />
            </Button>,
          ]}
        />

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Typography gutterBottom variant="h6" component="div">
            General
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-name"
                name="organization-name"
                label="Name"
                disabled={locked}
                value={name}
                onChange={(e: any) => setName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-domain"
                name="organization-domain"
                label="Domain"
                disabled={locked}
                value={domain}
                onChange={(e: any) => setDomain(e.target.value)}
                InputProps={{ endAdornment: domainIcon }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-acronym"
                name="organization-acronym"
                label="Acronym"
                disabled={locked}
                value={acronym}
                onChange={(e: any) => setAcronym(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-description"
                name="organization-description"
                label="Description"
                disabled={locked}
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-public-url"
                name="organization-public-url"
                label="Public URL"
                disabled={locked}
                value={urlPublic}
                onChange={(e: any) => setUrlPublic(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-private-url"
                name="organization-private-url"
                label="Private URL"
                disabled={locked}
                value={urlPrivate}
                onChange={(e: any) => setUrlPrivate(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="organization-dlm-push-url"
                name="organization-dlm-push-url"
                label="DLM push url"
                disabled={locked}
                value={dlmPushUrl}
                onChange={(e: any) => setDlmPushUrl(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="organization-dlm-verify-url"
                name="organization-dlm-verify-url"
                label="DLM verify url"
                disabled={locked}
                value={dlmVerifyUrl}
                onChange={(e: any) => setDlmVerifyUrl(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="organization-dlm-token"
                name="organization-dlm-token"
                label="DLM token"
                disabled={locked}
                value={dlmToken}
                onChange={(e: any) => setDlmToken(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography gutterBottom variant="h6" component="div">
                IDP
              </Typography>

              <DialogContentText variant="caption">
                To configure identity provider(s) for this organization please
                select IDP(s), then submit. Credentials must be provided for
                each identity provider. Changes will not take effect until
                submitted & confirmed. Note: Will ask for confirmation.
              </DialogContentText>

              <ProviderSelect idp={idp} setIdp={setIdp} disabled={locked} />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              variant={locked ? "outlined" : "contained"}
              onClick={() => setLocked(!locked)}
              sx={{ mt: 3, mb: 2, mr: 2 }}
            >
              {locked ? "Cancel" : "Save"}
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={!locked}
              sx={{ mt: 3, mb: 2 }}
            >
              {create.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Create"}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

const Create = ({ disabled = false }: { disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button
        color="secondary"
        variant="contained"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<CreateNewFolderIcon />}
      >
        Create
      </Button>

      <CreateView open={open} onClose={handleClose} />
    </div>
  );
};

export default Create;
